<template>
  <el-dialog
    v-model="modalVisible"
    :title="'Gestión de Cotización - ' + (cotizacion?.Codigo || '')"
    width="75%"
    center
    :close-on-click-modal="false"
    @close="closeModal"
  >
    <div
      v-loading="isLoading"
      class="dialog-content"
    >
      <ProposalsListItems
        v-if="cotizacion && !showRejectAnularForm"
        ref="proposalsListRef"
        :data="formattedCotizacionData"
        @require-products="handleRequireProducts"
        @approveItem="handleApproveItem"
        @deleteItem="handleDeleteItem"
      />

      <div
        v-if="showRejectAnularForm"
        class="reject-form-container"
      >
        <el-divider content-position="center">
          {{
            selectedAction === 'Rechazada'
              ? 'Rechazar Cotización Completa'
              : 'Anular Cotización Completa'
          }}
        </el-divider>
        <el-row :gutter="22">
          <el-col :span="10">
            <custom-select
              v-if="cotizacion"
              v-model="cotizacion.MotivoRechazo"
              :label="'Motivo de estado'"
              :settings="{
                placeholder: 'Seleccionar motivo',
                options: motivosList,
              }"
            ></custom-select>
          </el-col>
          <el-col :span="12">
            <div class="custom-label">Observaciones</div>
            <el-input
              v-if="cotizacion"
              v-model="cotizacion.ObservacionEstado"
              type="textarea"
              :rows="5"
              placeholder="Escriba aquí las observaciones..."
            >
            </el-input>
          </el-col>
        </el-row>

        <custom-confirm-buttons
          style="margin-top: 20px"
          :confirmText="'Guardar ' + selectedAction"
          :cancelText="'Cancelar'"
          @on-cancel="cancelRejectAnularForm"
          @on-confirm="onConfirmStateChange"
        ></custom-confirm-buttons>
      </div>

      <div
        v-if="!showRejectAnularForm"
        class="action-buttons-footer"
      >
        <el-divider></el-divider>
        <el-row
          justify="end"
          :gutter="24"
        >
          <el-col :span="3">
            <el-button
              type="danger"
              size="default"
              @click="promptStateAction('Rechazada')"
            >
              Rechazar Cotización
            </el-button>
          </el-col>
          <el-col :span="3">
            <el-button
              type="warning"
              size="default"
              @click="promptStateAction('Anulada')"
            >
              Anular Cotización
            </el-button>
          </el-col>
        </el-row>
      </div>
    </div>

    <ModalAsociarProductos
      v-if="cotizacion && showAsociarModal"
      v-model="showAsociarModal"
      :cotizacion="cotizacion"
      :cliente="cotizacion.Cliente || {}"
      :cantidadTotalAprobada="selectedRowAmount"
      :itemCantidadAprobada="selectedRowItemId"
      @on-success="onAsociarSuccess"
    />
  </el-dialog>
</template>

<script setup>
import {
  ref,
  watch,
  computed,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessageBox } from 'element-plus';
import { useRouter } from 'vue-router';

import { usePricesStore } from '@/views/prices/stores/prices_store.js';
import { useProposalsStore } from '../../proposals/proposals_store';
import { useMotivosStore } from '@/stores/motivos-rechazo.js';
import { useLoading } from '@/shared/composables/loading.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { EstadoSolicitud } from '@/enum/estado_solicitud';

import CustomSelect from '@/shared/components/custom_select.vue';
import CustomConfirmButtons from '@/shared/components/custom_confirm_buttons.vue';
import ProposalsListItems from '@/shared/components/proposals_list_items.vue';
import ModalAsociarProductos from '@/shared/components/modal-asociar-productos.vue';

const props = defineProps({
  selectedRow: Object,
  modelValue: Boolean,
});
const emit = defineEmits(['closed', 'update:modelValue']);

const router = useRouter();
const pricesStore = usePricesStore();
const motivosStore = useMotivosStore();
const proposalsStore = useProposalsStore();
const { approveCotizacionHTTP } = proposalsStore;
const { cotizacion } = storeToRefs(pricesStore);
const { newPriceNuevoHTTP, resetCotizacion } = pricesStore;
const { motivosList } = storeToRefs(motivosStore);
const { firstMotivos } = motivosStore;
const modalVisible = ref(false);
const isLoading = ref(false);
const showRejectAnularForm = ref(false);
const selectedAction = ref(null);
const proposalsListRef = ref(null);
const currentRowToApprove = ref(null);
const showAsociarModal = ref(false);
const selectedRowAmount = ref(0);
const selectedRowItemId = ref(0);

const formattedCotizacionData = computed(() => {
  if (!cotizacion.value || !cotizacion.value.CotizacionCantidad) return [];

  const estadoFresco = props.selectedRow?.Estado || cotizacion.value.Estado;
  const codigoEstadoFresco = props.selectedRow?.CodigoEstado || cotizacion.value.CodigoEstado;

  return [
    {
      ...cotizacion.value,
      Estado: estadoFresco,
      CodigoEstado: codigoEstadoFresco,
      items: cotizacion.value.CotizacionCantidad.map((item) => ({
        ...item,
        Cotizacion: {
          ...cotizacion.value,
          Estado: estadoFresco,
          CodigoEstado: codigoEstadoFresco,
        },
      })),
    },
  ];
});

watch(
  () => props.modelValue,
  (newValue) => {
    modalVisible.value = newValue;
    if (!newValue) {
      resetState();
    }
  },
);

watch(modalVisible, (newValue) => {
  emit('update:modelValue', newValue);
});

const resetState = () => {
  showRejectAnularForm.value = false;
  selectedAction.value = null;
  showAsociarModal.value = false;
  selectedRowAmount.value = 0;
  selectedRowItemId.value = 0;
};

const closeModal = () => {
  resetCotizacion();
  resetState();
  modalVisible.value = false;
  emit('update:modelValue', false);
  emit('closed');
};

const promptStateAction = (action) => {
  const isRechazo = action === 'Rechazada';
  const actionText = isRechazo ? 'rechazar' : 'anular';

  ElMessageBox.confirm(
    `¿Está seguro que desea ${actionText} la cotización? Esta acción afectará a TODAS las cantidades y no se podrá revertir.`,
    `Confirmar ${actionText}`,
    {
      confirmButtonText: `Sí, ${actionText}`,
      cancelButtonText: 'No, cancelar',
      type: isRechazo ? 'danger' : 'warning',
    },
  )
    .then(() => {
      selectedAction.value = action;
      showRejectAnularForm.value = true;

      if (cotizacion.value) {
        cotizacion.value.MotivoRechazo = '';
        cotizacion.value.ObservacionEstado = '';
        cotizacion.value.CodigoEstado = isRechazo
          ? EstadoSolicitud.RECHAZADA
          : EstadoSolicitud.ANULADA;
      }

      firstMotivos(isRechazo ? 'Rechazado' : 'Anulado');
    })
    .catch(() => {});
};

const cancelRejectAnularForm = () => {
  showRejectAnularForm.value = false;
  selectedAction.value = null;
};

const onConfirmStateChange = async () => {
  try {
    useLoading().openLoading();

    await newPriceNuevoHTTP();

    useMessageSuccess().open(
      `La cotización fue ${selectedAction.value.toLowerCase()} exitosamente.`,
    );
    closeModal();
    router.go(0);
  } catch (err) {
    useMessageError().open(`Ha ocurrido un error al intentar cambiar el estado de la cotización.`);
  } finally {
    useLoading().closeLoading();
  }
};

const handleRequireProducts = (row) => {
  currentRowToApprove.value = row;
  selectedRowAmount.value = row.Cantidad;
  selectedRowItemId.value = row.Codigo;
  showAsociarModal.value = true;
};

const onAsociarSuccess = (productosAsociados) => {
  showAsociarModal.value = false;

  if (currentRowToApprove.value && currentRowToApprove.value.Cotizacion) {
    currentRowToApprove.value.Cotizacion.CotizacionProducto = productosAsociados;
  }

  if (proposalsListRef.value) {
    proposalsListRef.value.continueApproval(currentRowToApprove.value);
  }
};
const handleApproveItem = async (payload) => {
  try {
    useLoading().openLoading();
    await approveCotizacionHTTP(payload);

    useMessageSuccess().open(
      `La cotización ${payload.CodigoCotizacion} fue aprobada exitosamente.`,
    );

    closeModal();
    router.go(0);
  } catch (error) {
    console.error(error);
    useMessageError().open(`Ha ocurrido un error al intentar aprobar la cotización.`);
  } finally {
    useLoading().closeLoading();
  }
};
const handleDeleteItem = (payload) => {
  const { indexCantidad } = payload;
  if (cotizacion.value && cotizacion.value.CotizacionCantidad) {
    cotizacion.value.CotizacionCantidad.splice(indexCantidad, 1);
  }
};
</script>

<style scoped>
.dialog-content {
  min-height: 250px;
}
.reject-form-container {
  margin-top: 15px;
  background-color: #fcfcfc;
  padding: 15px 20px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
}
.action-buttons-footer {
  margin-top: 30px;
}
.custom-label {
  font-weight: 700;
  color: #111;
  margin-bottom: 5px;
}
</style>
